import api from './client'
import type { AgentConnectStatus, ClaudeOauthStatus, CodexDeviceFlow } from './settings'

export interface PlatformAiSettings {
  mode: string
  provider: string
  /** Agent CLI run in "agent" mode: claude_code | codex. */
  agent: string
  model: string
  effort: string
  /** Agent-mode auth policy: "subscription" | "api_key". */
  auth: string
  /** Which agents have a platform subscription credential stored. */
  agent_credentials: Record<string, boolean>
  /** Per-task {model, effort} overrides on top of the platform provider/mode. */
  task_llm_config: Record<string, { model?: string; effort?: string }>
  has_api_key: boolean
  /** Whether a platform-default Claude subscription is connected (OAuth). */
  has_claude_oauth: boolean
  basic_monthly_tokens: number
  pro_monthly_tokens: number
  updated_by: string
  updated_at: string | null
}

export interface PlatformAiSettingsUpdate {
  mode?: string
  provider?: string
  agent?: string
  model?: string
  api_key?: string // omit = unchanged, "" = clear
  effort?: string
  /** Agent-mode auth policy: "subscription" | "api_key". */
  auth?: string
  /** omit = unchanged, {} = clear all per-task overrides */
  task_llm_config?: Record<string, { model?: string; effort?: string }>
  basic_monthly_tokens?: number
  pro_monthly_tokens?: number
}

export async function getPlatformAiSettings(): Promise<PlatformAiSettings> {
  const { data } = await api.get('/instance-admin/ai-settings')
  return data
}

export async function updatePlatformAiSettings(payload: PlatformAiSettingsUpdate): Promise<PlatformAiSettings> {
  const { data } = await api.put('/instance-admin/ai-settings', payload)
  return data
}

/**
 * Store (or, with a blank credential, clear) the platform SUBSCRIPTION
 * credential for an agent. Claude Code has the in-app OAuth flow below; Codex
 * and Gemini have no equivalent, so the admin pastes the CLI's credentials
 * file (~/.codex/auth.json) — the same thing
 * Settings → LLM asks a user for.
 */
export async function setPlatformAgentCredential(
  agent: string,
  credential: string,
): Promise<PlatformAiSettings> {
  const { data } = await api.put('/instance-admin/ai-settings/agent-credential', {
    agent,
    credential,
  })
  return data
}

/* ── Platform Claude subscription (OAuth connect) ───────────────────── */
// Same server-side `claude setup-token` pty flow as Settings → LLM, but the
// captured token becomes the PLATFORM default every user riding it runs on.
// See backend routers/instance_admin.py + services/claude_oauth_service.py.

export async function startPlatformClaudeOauth(): Promise<{ flow_id: string; auth_url: string }> {
  const { data } = await api.post('/instance-admin/ai-settings/claude-oauth/start')
  return data
}

export async function completePlatformClaudeOauth(
  flowId: string,
  code: string,
): Promise<{ ok: boolean; validated: boolean; status: ClaudeOauthStatus }> {
  const { data } = await api.post('/instance-admin/ai-settings/claude-oauth/complete', {
    flow_id: flowId,
    code,
  })
  return data
}

export async function cancelPlatformClaudeOauth(flowId: string): Promise<void> {
  await api.post('/instance-admin/ai-settings/claude-oauth/cancel', { flow_id: flowId })
}

export async function getPlatformClaudeOauthStatus(): Promise<ClaudeOauthStatus> {
  const { data } = await api.get('/instance-admin/ai-settings/claude-oauth/status')
  return data
}

export async function disconnectPlatformClaudeOauth(): Promise<void> {
  await api.delete('/instance-admin/ai-settings/claude-oauth')
}

// Same server-side `codex login --device-auth` flow as Settings → LLM, but the
// captured credential becomes the PLATFORM default every user riding it runs
// on. See backend routers/instance_admin.py + services/codex_oauth_service.py.

export async function startPlatformCodexOauth(): Promise<CodexDeviceFlow> {
  const { data } = await api.post('/instance-admin/ai-settings/codex-oauth/start')
  return data
}

export async function pollPlatformCodexOauth(
  flowId: string,
): Promise<{ state: 'pending' | 'connected'; status?: AgentConnectStatus }> {
  const { data } = await api.post('/instance-admin/ai-settings/codex-oauth/poll', {
    flow_id: flowId,
  })
  return data
}

export async function cancelPlatformCodexOauth(flowId: string): Promise<void> {
  await api.post('/instance-admin/ai-settings/codex-oauth/cancel', { flow_id: flowId })
}

export async function getPlatformCodexOauthStatus(): Promise<AgentConnectStatus> {
  const { data } = await api.get('/instance-admin/ai-settings/codex-oauth/status')
  return data
}

export async function disconnectPlatformCodexOauth(): Promise<void> {
  await api.delete('/instance-admin/ai-settings/codex-oauth')
}

/* ── Release stage + waitlist access grants ─────────────────────────── */

export interface ReleaseStageInfo {
  /** "beta" (waitlist only) | "production" (open sign-up). */
  release_stage: string
  signup_enabled: boolean
  pending_waitlist?: number
}

/**
 * Where an invited person actually got to. `status` is the grant (what the
 * admin did); `account_state` is the acceptance (what the person did) — the
 * two are independent, so a GRANTED entry can still be sitting at "pending".
 */
export type WaitlistAccountState = 'none' | 'pending' | 'active' | 'deactivated'

export interface WaitlistEntry {
  id: number
  email: string
  name: string
  organization: string
  application_idea: string
  /** 2-letter ISO code from the join form; copied onto the user at grant. */
  country: string | null
  status: string // PENDING | GRANTED
  created_at: string | null
  granted_at: string | null
  granted_by: string | null
  user_id: string | null
  /** none = no account yet, pending = link never used, active = activated. */
  account_state: WaitlistAccountState
  account_active: boolean
  /** true while activation still has to collect a password. */
  must_set_password: boolean
  last_login_at: string | null
}

/* ── Platform tenant-data database ──────────────────────────────────── */
// The Postgres server hosting twasta_preview_<tenant> for every project with
// no connection of its own. Unconfigured, the platform parses DATABASE_URL
// (the auth/identity URL) — `defaults` is what the form pre-fills with: the
// auth server's host/port plus the suggested `twaadmin` login.

export interface PlatformDataDb {
  /** false = still falling back to DATABASE_URL; the fields below are defaults. */
  configured: boolean
  /** true while the instance is on the .env DATABASE_URL (the inverse of `configured`). */
  use_env: boolean
  /** What the .env DATABASE_URL fallback actually resolves to. */
  env: { host: string; port: number; username: string; database: string }
  host: string
  port: number
  username: string
  /** Maintenance DB used to CREATE the per-tenant databases. */
  admin_db: string
  has_password: boolean
  defaults: { host: string; port: number; username: string; admin_db: string }
  updated_by: string
}

export interface PlatformDataDbUpdate {
  host: string
  port?: number
  username?: string
  password?: string // omit = unchanged, "" = clear
  admin_db?: string
}

export async function getPlatformDataDb(): Promise<PlatformDataDb> {
  const { data } = await api.get('/instance-admin/data-db')
  return data
}

export async function updatePlatformDataDb(payload: PlatformDataDbUpdate): Promise<PlatformDataDb> {
  const { data } = await api.put('/instance-admin/data-db', payload)
  return data
}

/** Revert to the DATABASE_URL fallback. */
export async function clearPlatformDataDb(): Promise<PlatformDataDb> {
  const { data } = await api.delete('/instance-admin/data-db')
  return data
}

/** Connectivity check without saving; a blank password reuses the stored one. */
export async function testPlatformDataDb(
  payload: PlatformDataDbUpdate,
): Promise<{ success: boolean; message: string }> {
  const { data } = await api.post('/instance-admin/data-db/test', payload)
  return data
}

/* ── Outbound email (SMTP) ──────────────────────────────────────────── */
// Console-owned overrides on the MAIL_* env settings. `configured: false`
// means the instance is still on env and the fields shown are those defaults.

export interface PlatformEmailSettings {
  configured: boolean
  server: string
  port: number
  use_tls: boolean
  username: string
  from_address: string
  has_password: boolean
  defaults: { server: string; port: number; use_tls: boolean; username: string; from_address: string }
}

export interface PlatformEmailUpdate {
  server: string
  port?: number
  use_tls?: boolean
  username?: string
  password?: string // omit = unchanged, "" = clear
  from_address?: string
}

export async function getPlatformEmail(): Promise<PlatformEmailSettings> {
  const { data } = await api.get('/instance-admin/email')
  return data
}

export async function updatePlatformEmail(payload: PlatformEmailUpdate): Promise<PlatformEmailSettings> {
  const { data } = await api.put('/instance-admin/email', payload)
  return data
}

/** Revert to the MAIL_* env settings. */
export async function clearPlatformEmail(): Promise<PlatformEmailSettings> {
  const { data } = await api.delete('/instance-admin/email')
  return data
}

/** Send a test message through the given (or stored) settings, without saving. */
export async function testPlatformEmail(
  payload: Partial<PlatformEmailUpdate> & { to: string },
): Promise<{ success: boolean; message: string }> {
  const { data } = await api.post('/instance-admin/email/test', payload)
  return data
}

/* ── reCAPTCHA (the site-level bot check) ───────────────────────────── */
// The instance-wide FALLBACK key pair. It protects the public entry points of
// every project that has not configured its own in Advanced → Spam protection.
// It covers this platform's own hostnames only — a reCAPTCHA v3 key is bound
// to a domain list, so a project served at a customer-owned domain configures
// its own.

export interface PlatformCaptchaSettings {
  provider: string
  configured: boolean
  site_key: string
  has_secret: boolean
  min_score: number
  /** False when a site key is stored with no secret — a check that runs in
   *  every browser and is never verified. */
  usable: boolean
}

export interface PlatformCaptchaUpdate {
  site_key?: string
  secret_key?: string // omit = unchanged, "" = clear
  min_score?: number
}

export async function getPlatformCaptcha(): Promise<PlatformCaptchaSettings> {
  const { data } = await api.get('/instance-admin/captcha')
  return data
}

export async function updatePlatformCaptcha(
  payload: PlatformCaptchaUpdate,
): Promise<PlatformCaptchaSettings> {
  const { data } = await api.put('/instance-admin/captcha', payload)
  return data
}

/** Turn the site-level check off. Projects with their own keys keep theirs. */
export async function clearPlatformCaptcha(): Promise<PlatformCaptchaSettings> {
  const { data } = await api.delete('/instance-admin/captcha')
  return data
}

/** Prove the STORED secret is real, without a browser token. */
export async function testPlatformCaptcha(): Promise<{ success: boolean; message: string }> {
  const { data } = await api.post('/instance-admin/captcha/test')
  return data
}

/* ── Payment gateway (Razorpay) ─────────────────────────────────────── */

export interface PlatformPaymentSettings {
  configured: boolean
  provider: string
  key_id: string
  has_key_secret: boolean
  /** The admin's on/off intent. */
  payments_enabled: boolean
  /** Whether checkout will actually work right now (intent AND usable keys). */
  effective_enabled: boolean
  defaults: { provider: string; key_id: string }
}

export interface PlatformPaymentUpdate {
  key_id?: string
  key_secret?: string // omit = unchanged, "" = clear
  payments_enabled?: boolean
}

export async function getPlatformPayments(): Promise<PlatformPaymentSettings> {
  const { data } = await api.get('/instance-admin/payments')
  return data
}

export async function updatePlatformPayments(payload: PlatformPaymentUpdate): Promise<PlatformPaymentSettings> {
  const { data } = await api.put('/instance-admin/payments', payload)
  return data
}

/** Verify the stored credentials against Razorpay with a read-only call. */
export async function testPlatformPayments(): Promise<{ success: boolean; message: string }> {
  const { data } = await api.post('/instance-admin/payments/test')
  return data
}

/* ── Agent security policy ──────────────────────────────────────────────
 * The token-authenticated agent surfaces (the SSH console and the laptop
 * agent) plus the auth-cookie Secure policy. Every field is optional and
 * every unset field keeps the behaviour that was hardcoded before this
 * panel existed — see docs/agent_surface_hardening_plan.md.
 */

export interface AgentSecurityPolicy {
  /** null = non-expiring agent tokens are permitted. */
  agent_token_max_ttl_days: number | null
  agent_token_default_ttl_days: number | null
  agent_session_ttl_seconds: number
  /** Comma-separated subset of "ssh,remote"; "" = both disabled. */
  agent_surfaces_enabled: string
  agent_require_delegated_db: boolean
  cookie_secure: 'auto' | 'always' | 'never'
  /** Which fields the admin has actually set (vs. defaulted). */
  explicit: Record<string, boolean>
  defaults: Partial<AgentSecurityPolicy>
  surfaces: string[]
  cookie_secure_modes: string[]
}

export interface AgentSecurityUpdate {
  /** 0 clears a numeric field back to its default; omit = unchanged. */
  agent_token_max_ttl_days?: number
  agent_token_default_ttl_days?: number
  agent_session_ttl_seconds?: number
  agent_surfaces_enabled?: string
  agent_require_delegated_db?: boolean
  cookie_secure?: string
}

export async function getAgentSecurity(): Promise<AgentSecurityPolicy> {
  const { data } = await api.get('/instance-admin/agent-security')
  return data
}

export async function updateAgentSecurity(
  payload: AgentSecurityUpdate,
): Promise<AgentSecurityPolicy> {
  const { data } = await api.put('/instance-admin/agent-security', payload)
  return data
}

/** Give every live non-expiring agent token an expiry, now. */
export async function expireNonExpiringAgentTokens(): Promise<{ expired: number }> {
  const { data } = await api.post('/instance-admin/agent-security/expire-tokens')
  return data
}

/* ── Query execution (analytical statement timeout) ─────────────────────
 * The instance-wide cap on how long ANY generated analytical query may run
 * (dashboards, visuals, reports, Ask-AI design-query runs) before the
 * database cancels it. Unset = the built-in per-path defaults (60s on the
 * BI/external path, 15s on the design-query live preview).
 */

export interface QueryExecutionSettings {
  /** The resolved value in force — the explicit one, or the built-in default. */
  query_timeout_seconds: number
  /** The ceiling a project connection's own QUERY_TIMEOUT_SECONDS may not
   *  exceed. Also served (resolved) on GET /connections/types, which is how a
   *  non-admin user learns it. */
  max_query_timeout_seconds: number
  /** Which fields the admin has actually set (vs. defaulted). */
  explicit: { query_timeout_seconds: boolean; max_query_timeout_seconds: boolean }
  defaults: { query_timeout_seconds: number; max_query_timeout_seconds: number }
}

export interface QueryExecutionUpdate {
  /** 0 clears the field back to its default; omit = unchanged. */
  query_timeout_seconds?: number
  max_query_timeout_seconds?: number
}

export async function getQueryExecution(): Promise<QueryExecutionSettings> {
  const { data } = await api.get('/instance-admin/query-execution')
  return data
}

export async function updateQueryExecution(
  payload: QueryExecutionUpdate,
): Promise<QueryExecutionSettings> {
  const { data } = await api.put('/instance-admin/query-execution', payload)
  return data
}

/* ── Resource limits (what one application may use of the shared server) ──
 * The instance-wide CEILINGS no hosting plan may exceed, how a plan's
 * per-user allowance counts users, what the watchdog does to an application
 * that keeps going over, and the live per-application state it keeps.
 */

export type ResourceLimitScope = 'app' | 'tenant_db' | 'global'

export interface ResourceLimitDef {
  label: string
  group: string
  scope: ResourceLimitScope
  unit: string
  default: number
  min: number
  ceiling: number
  /** Whether a plan may add a per-user allowance on top of its base. */
  scales: boolean
  hint: string
}

export interface ResourceLimitCatalog {
  groups: { id: string; label: string }[]
  limits: Record<string, ResourceLimitDef>
  user_basis: string[]
  watchdog_actions: string[]
}

export interface ResourceLimitPolicy {
  ceilings: Record<string, number>
  user_basis: string
  watchdog_action: string
  alert_emails: string[]
}

export interface PlanOverCeiling {
  code: string
  display_name: string
  problems: string[]
}

export interface ResourceLimitsSettings {
  catalog: ResourceLimitCatalog
  policy: ResourceLimitPolicy
  plans_over_ceiling: PlanOverCeiling[]
}

export type ResourceState = 'ok' | 'throttled' | 'alerted' | 'suspended'

export interface ResourceStatusRow {
  owner_user_id: string
  project_id: string
  tenant_id: string
  state: ResourceState
  reason: string | null
  throttled_until: string | null
  rejected_requests: number
  busy_refusals: number
  cancelled_queries: number
  terminated_sessions: number
  last_alert_at: string | null
  updated_at: string | null
  /** The row describes the whole organization's database, not one application. */
  tenant_wide: boolean
  running?: { report: number; process: number; import: number; background: number }
}

export interface ResourceJobState {
  last_run_at?: string | null
  last_status?: string | null
  /** The job run's outcome; `error` is set when the last run failed. */
  detail?: { error?: string } & Record<string, unknown>
  [key: string]: unknown
}

export interface ResourceStatus {
  rows: ResourceStatusRow[]
  jobs: { resource_watchdog?: ResourceJobState; resource_role_reconcile?: ResourceJobState }
}

export interface ResourceLimitExplainEntry {
  value: number
  base: number | null
  per_user: number | null
  users: number | null
  plan_max: number | null
  ceiling: number | null
  capped_by: 'plan' | 'plan_max' | 'ceiling' | 'min' | 'default'
}

export interface ResourceLimitExplain {
  plan: { code: string; display_name: string }
  user_basis: string
  users: number
  tenant: string | null
  limits: Record<string, ResourceLimitExplainEntry>
}

export async function getResourceLimits(): Promise<ResourceLimitsSettings> {
  const { data } = await api.get('/instance-admin/resource-limits')
  return data
}

export async function updateResourceLimits(
  payload: ResourceLimitPolicy,
): Promise<{ policy: ResourceLimitPolicy; plans_over_ceiling: PlanOverCeiling[] }> {
  const { data } = await api.put('/instance-admin/resource-limits', payload)
  return data
}

export async function getResourceStatus(): Promise<ResourceStatus> {
  const { data } = await api.get('/instance-admin/resource-limits/status')
  return data
}

export async function clearResourceStatus(row: {
  owner_user_id: string; project_id: string; tenant_id: string
}): Promise<void> {
  await api.post('/instance-admin/resource-limits/status/clear', row)
}

export async function explainResourceLimits(params: {
  owner_user_id: string; project: string; tenant_id?: string
}): Promise<ResourceLimitExplain> {
  const { data } = await api.get('/instance-admin/resource-limits/explain', { params })
  return data
}

/** Re-apply the per-organization database limits now (normally a background job). */
export async function reconcileResourceLimits(): Promise<{
  tenants: number; changed: number; failed: number
}> {
  const { data } = await api.post('/instance-admin/resource-limits/reconcile')
  return data
}

export async function getReleaseStage(): Promise<ReleaseStageInfo> {
  const { data } = await api.get('/instance-admin/release-stage')
  return data
}

export async function updateReleaseStage(stage: string): Promise<ReleaseStageInfo> {
  const { data } = await api.put('/instance-admin/release-stage', { release_stage: stage })
  return data
}

export async function getWaitlist(params: {
  status?: string
  /** Acceptance filter: none | pending | active | deactivated. */
  account?: WaitlistAccountState
  q?: string
  limit?: number
  offset?: number
} = {}): Promise<{ total: number; items: WaitlistEntry[] }> {
  const { data } = await api.get('/instance-admin/waitlist', { params })
  return data
}

export async function grantWaitlistAccess(entryId: number): Promise<{
  granted: boolean
  email_sent: boolean
  already_active?: boolean
  message: string
  entry: WaitlistEntry
}> {
  const { data } = await api.post(`/instance-admin/waitlist/${entryId}/grant`)
  return data
}

/* ── Platform-wide user directory + deactivation ────────────────────── */
// Instance admins can deactivate ANY user in ANY tenant. A tenant
// (enterprise) admin has the same control but only over their own tenant —
// see api/enterprise.ts `updateUserStatus`.

export interface InstanceUser {
  user_id: string
  user_name: string
  email: string
  role: string
  role_display: string
  tenant_id: string
  tenant_name: string
  /** false = deactivated; the account cannot sign in at all. */
  is_active: boolean
  /** Email-verification state: "A" verified, "N" never activated. */
  status: string
  last_login_at: string | null
  deactivated_at: string | null
  deactivated_by_name: string | null
  /** Platform operator (INSTANCE_ADMIN_IDS) — cannot be deactivated here. */
  is_instance_admin: boolean
  /** Hard-locked after too many failed sign-ins. */
  locked?: boolean
  /** Has an authenticator app set up. */
  mfa_enabled?: boolean
  is_current_user: boolean
}

export interface InstanceUserPage {
  total: number
  limit: number
  offset: number
  items: InstanceUser[]
  tenants: { tenant_id: string; tenant_name: string }[]
}

export async function getInstanceUsers(params: {
  q?: string
  tenant_id?: string
  status?: 'active' | 'inactive' | ''
  limit?: number
  offset?: number
} = {}): Promise<InstanceUserPage> {
  const { data } = await api.get('/instance-admin/users', { params })
  return data
}

/** Clear any user's sign-in lock (too many failed attempts). */
export async function unlockInstanceUser(
  userId: string,
): Promise<{ success: boolean; was_locked: boolean }> {
  const { data } = await api.post(`/instance-admin/users/${userId}/unlock`)
  return data
}

/** Remove any user's second factor (lost phone) and sign them out. */
export async function resetInstanceUserMfa(
  userId: string,
): Promise<{ success: boolean; removed: boolean; sessions_ended: number }> {
  const { data } = await api.post(`/instance-admin/users/${userId}/mfa/reset`)
  return data
}

export async function setInstanceUserStatus(
  userId: string,
  isActive: boolean,
  reason?: string,
): Promise<{ success: boolean; is_active: boolean; sessions_ended?: number }> {
  const { data } = await api.put(`/instance-admin/users/${userId}/status`, {
    is_active: isActive,
    reason,
  })
  return data
}

export interface InstanceAnalytics {
  days: number
  engagement: {
    total_users: number
    active_users: number
    new_signups: number
    signups_daily: { date: string; count: number }[]
    plan_distribution: { plan: string; label: string; count: number }[]
  }
  revenue: {
    total: number
    paying_customers: number
    daily: { date: string; amount: number }[]
  }
  tokens: {
    total_tokens: number
    platform_tokens: number
    own_key_tokens: number
    total_cost_usd: number
    platform_cost_usd: number
    calls: number
    daily: { date: string; tokens: number; cost_usd: number }[]
    by_model: { model: string; provider: string; tokens: number; cost_usd: number; calls: number }[]
    top_users: { user_id: string; tokens: number; cost_usd: number; calls: number }[]
  }
}

export async function getInstanceAnalytics(days = 30): Promise<InstanceAnalytics> {
  const { data } = await api.get('/instance-admin/analytics', { params: { days } })
  return data
}

/* ── IOFlow integration delivery health ─────────────────────────────── */

export interface IOFlowHealth {
  days: number
  pool: {
    running: boolean
    pool_size?: number
    active_projects?: number
    ready_depth?: number
    inflight?: number
    retry_queued?: number
  }
  totals: {
    incidents: number
    failed: number
    terminal: number
    projects_affected: number
  }
  projects: Array<{
    owner_user_id: string
    project_id: string
    incidents: number
    failed: number
    terminal: number
    attempts_max: number
    last_at: string | null
  }>
}

export interface IOFlowFailure {
  id: number
  owner_user_id: string
  project_id: string
  tenant_id: string | null
  ioflow_app_id: string | null
  failed_count: number
  delivered_count: number
  terminal_count: number
  attempts_max: number
  sample_event: string | null
  error: string | null
  created_at: string | null
}

export async function getIOFlowHealth(days = 7): Promise<IOFlowHealth> {
  const { data } = await api.get('/instance-admin/ioflow/health', { params: { days } })
  return data
}

export async function getIOFlowFailures(params: {
  project?: string
  owner?: string
  limit?: number
  offset?: number
} = {}): Promise<{ total: number; items: IOFlowFailure[] }> {
  const { data } = await api.get('/instance-admin/ioflow/failures', { params })
  return data
}

export async function retryIOFlowDelivery(ownerUserId: string, projectId: string): Promise<{ queued: boolean }> {
  const { data } = await api.post('/instance-admin/ioflow/retry', {
    owner_user_id: ownerUserId,
    project_id: projectId,
  })
  return data
}

/* ── IOFlow connector publish approvals ──────────────────────────── */

/**
 * A request to publish a GLOBAL IOFlow connector — one shared with every
 * tenant on the instance, which is why IOFlow gates it on INSTANCE_ADMIN.
 * Approving publishes it with the APPROVER's own IOFlow credentials and
 * resumes the requester's paused agent session.
 */
export interface IOFlowPublishRequest {
  id: string
  app_key: string
  app_name: string | null
  project: string
  requester_user_id: string
  tenant_id: string | null
  status: 'pending' | 'approved' | 'denied' | 'expired'
  justification: string | null
  denial_reason: string | null
  approver_user_id: string | null
  notified: string | null
  created_at: string | null
  expires_at: string | null
  decided_at: string | null
  /** Whether a paused agent session can actually be continued afterwards. */
  resumable: boolean
  resumed: boolean
  resume_error: string | null
  install_error: string | null
  seed_json: string
}

export async function getIOFlowPublishRequests(
  status = '',
): Promise<{ requests: IOFlowPublishRequest[]; expired_now: number }> {
  const { data } = await api.get('/instance-admin/ioflow/publish-requests', {
    params: status ? { status } : {},
  })
  return data
}

export async function approveIOFlowPublish(requestId: string) {
  const { data } = await api.post(
    `/instance-admin/ioflow/publish-requests/${requestId}/approve`,
  )
  return data as { ok: boolean; status?: string; message?: string; error?: string }
}

export async function denyIOFlowPublish(requestId: string, reason: string) {
  const { data } = await api.post(
    `/instance-admin/ioflow/publish-requests/${requestId}/deny`,
    { reason },
  )
  return data as { ok: boolean; status?: string; message?: string }
}

/* ── Organization directory ──────────────────────────────────────────── */
// Read-only listing. Editing a row goes through `updateTenant` in
// api/enterprise.ts — the same endpoint a tenant's own admin uses, so
// validation and the audit trail have one implementation.

export interface InstanceTenant {
  tenant_id: string
  tenant_name: string
  email_domain: string
  web_site: string
  gst_no: string
  /** ibase SSO claim mapped to this organization; "" = unmapped. */
  enterprise: string
  owner_name: string
  owner_email: string
  user_count: number
}

export async function getInstanceTenants(q?: string): Promise<{ items: InstanceTenant[] }> {
  const { data } = await api.get('/instance-admin/tenants', { params: q ? { q } : undefined })
  return data
}

/* ── Image generation (the platform image key) ───────────────────────── */
// What an organization's AI credits buy pictures with (System administration
// → AI → Image generation → AI Credit), and what a builder without their own
// image key falls back to. The key is write-only.
export interface PlatformImageGenSettings {
  provider: string
  model: string
  has_key: boolean
  providers: string[]
  models: Record<string, string[]>
  default_models: Record<string, string>
  /** Whether AI Credit can make images at all (incl. the derived fallback). */
  available: boolean
  /** "image" = this dedicated setting, "bundle" = derived from the platform AI. */
  effective_source: '' | 'image' | 'bundle'
  effective_provider: string
  effective_model: string
}

export interface PlatformImageGenUpdate {
  provider?: string
  model?: string
  api_key?: string   // omitted = unchanged, "" = clear
}

export async function getPlatformImageGen(): Promise<PlatformImageGenSettings> {
  const { data } = await api.get('/instance-admin/image-generation')
  return data
}

export async function updatePlatformImageGen(
  payload: PlatformImageGenUpdate,
): Promise<PlatformImageGenSettings> {
  const { data } = await api.put('/instance-admin/image-generation', payload)
  return data
}

export async function clearPlatformImageGen(): Promise<PlatformImageGenSettings> {
  const { data } = await api.delete('/instance-admin/image-generation')
  return data
}

export async function testPlatformImageGen(
  payload: PlatformImageGenUpdate,
): Promise<{ ok: boolean; message: string }> {
  const { data } = await api.post('/instance-admin/image-generation/test', payload)
  return data
}

// ── Edge & HTTPS (certificates + the web servers that serve them) ──────────

export type EdgeMode = 'external' | 'nginx' | 'caddy' | 'cloudflare'

export interface EdgeSettings {
  mode: EdgeMode
  modes: EdgeMode[]
  acme_email: string
  acme_directory: string // "production" | "staging" | https URL
  acme_registered: boolean
  platform_hostnames: string[]
  extra_hostnames: string
  upstream: string
  max_body_mb: number
  cloudflare_zone_id: string
  cloudflare_cname_target: string
  has_cloudflare_token: boolean
  revision: number
  schedule: EdgeSchedule
  /** Trust X-Forwarded-* from registered edge servers automatically. */
  auto_trust: boolean
  /** Serve FRONTEND_URL's host through the generated config too. */
  serve_platform_host: boolean
  frontend_host: string
  trusted_proxy_ips: string
}

export interface EdgeSettingsUpdate {
  mode?: EdgeMode
  acme_email?: string
  acme_directory?: string
  extra_hostnames?: string
  upstream?: string
  max_body_mb?: number
  cloudflare_zone_id?: string
  cloudflare_token?: string // omit = unchanged, "" = clear
  cloudflare_cname_target?: string
  auto_trust?: boolean
  serve_platform_host?: boolean
}

export interface EdgeCertificate {
  id: number
  hostnames: string[]
  source: 'acme' | 'upload' | 'cloudflare_origin'
  status: 'pending' | 'issuing' | 'active' | 'failed' | 'revoked'
  not_before: string | null
  not_after: string | null
  days_left: number | null
  attempts: number
  next_attempt_at: string | null
  last_error: string | null
  project_domain_id: number | null
  updated_at: string | null
}

export interface EdgeSchedule {
  enabled: boolean
  interval_s: number
  retry_base_s: number
  retry_max_s: number
  offline_after_s: number
  push_timeout_s: number
  alert_after_min: number
  alert_admins: boolean
  alert_managers: boolean
  alert_extra_emails: string
}

export interface EdgeServerInfo {
  id: number
  name: string
  description: string | null
  engine: 'nginx' | 'caddy'
  connection: 'push' | 'pull'
  agent_url: string | null
  cert_dir: string
  enabled: boolean
  status: 'new' | 'in_sync' | 'pending' | 'failed' | 'offline' | 'disabled'
  applied_revision: number
  current_revision: number
  in_sync: boolean
  agent_version: string | null
  engine_version: string | null
  last_contact_at: string | null
  last_success_at: string | null
  last_error: string | null
  /** Applied, but nginx said something (e.g. a hand-written site claims the same host). */
  last_warning: string | null
  /** The Twasta application server itself. */
  is_local: boolean
  last_seen_ip: string | null
  /** An install link is outstanding (not yet used, not expired). */
  install_pending: boolean
  install_expires_at: string | null
  consecutive_failures: number
  failing_since: string | null
  alerted_at: string | null
}

/** The one-command install: `curl -fsSL <url> | sudo bash`. */
export interface EdgeInstallLink {
  command: string
  url: string
  expires_at: string
  is_local: boolean
}

export interface EdgeServerInput {
  name?: string
  description?: string
  engine?: 'nginx' | 'caddy'
  connection?: 'push' | 'pull'
  agent_url?: string
  cert_dir?: string
  is_local?: boolean
}

/** Shown ONCE — after registering a server or rotating its token. */
export interface EdgeInstall {
  token: string
  agent_json: string
  steps: string[]
}

export interface EdgeDeliveryInfo {
  id: number
  revision: number
  status: 'pending' | 'sending' | 'success' | 'failed' | 'superseded'
  attempts: number
  error: string | null
  next_attempt_at: string | null
  last_attempt_at: string | null
  delivered_at: string | null
  duration_ms: number | null
  created_at: string | null
  summary: string | null
}

export interface EdgePropagation {
  revision: number
  servers: EdgeServerInfo[]
  affected_projects: {
    owner: string
    project: string
    hostnames: string[]
    servers: string[]
    since: string | null
    changes: string[]
  }[]
}

export async function getEdgeSettings(): Promise<EdgeSettings> {
  const { data } = await api.get('/instance-admin/edge')
  return data
}

export async function updateEdgeSettings(payload: EdgeSettingsUpdate): Promise<EdgeSettings> {
  const { data } = await api.put('/instance-admin/edge', payload)
  return data
}

/** ACME modes: reach the directory + register the account. Cloudflare: read the zone. */
export async function testEdgeSettings(): Promise<Record<string, unknown>> {
  const { data } = await api.post('/instance-admin/edge/test')
  return data
}

export async function previewEdgeConfig(engine?: 'nginx' | 'caddy'): Promise<{
  engine: string; revision: number; config: string; skipped: string[]
}> {
  const { data } = await api.get('/instance-admin/edge/preview', { params: engine ? { engine } : {} })
  return data
}

export async function getEdgeServers(): Promise<{ revision: number; servers: EdgeServerInfo[] }> {
  const { data } = await api.get('/instance-admin/edge/servers')
  return data
}

export async function addEdgeServer(body: EdgeServerInput): Promise<{ server: EdgeServerInfo; install_link: EdgeInstallLink }> {
  const { data } = await api.post('/instance-admin/edge/servers', body)
  return data
}

/** A fresh one-command install link (re-install, or the old one expired). */
export async function newEdgeInstallLink(id: number): Promise<{ server: EdgeServerInfo; install_link: EdgeInstallLink }> {
  const { data } = await api.post(`/instance-admin/edge/servers/${id}/install-link`)
  return data
}

export async function updateEdgeServer(id: number, body: EdgeServerInput): Promise<EdgeServerInfo> {
  const { data } = await api.put(`/instance-admin/edge/servers/${id}`, body)
  return data.server
}

export async function rotateEdgeServerToken(id: number): Promise<{ server: EdgeServerInfo; install: EdgeInstall }> {
  const { data } = await api.post(`/instance-admin/edge/servers/${id}/rotate-token`)
  return data
}

export async function setEdgeServerEnabled(id: number, enabled: boolean): Promise<EdgeServerInfo> {
  const { data } = await api.post(`/instance-admin/edge/servers/${id}/enabled`, { enabled })
  return data.server
}

export async function removeEdgeServer(id: number): Promise<void> {
  await api.delete(`/instance-admin/edge/servers/${id}`)
}

export async function pushEdgeServer(id: number): Promise<EdgeServerInfo> {
  const { data } = await api.post(`/instance-admin/edge/servers/${id}/push`)
  return data.server
}

export async function checkEdgeServer(id: number): Promise<{ server: EdgeServerInfo; message?: string }> {
  const { data } = await api.post(`/instance-admin/edge/servers/${id}/check`)
  return data
}

export async function getEdgeServerDeliveries(id: number): Promise<EdgeDeliveryInfo[]> {
  const { data } = await api.get(`/instance-admin/edge/servers/${id}/deliveries`)
  return data.deliveries
}

export async function getEdgePropagation(): Promise<EdgePropagation> {
  const { data } = await api.get('/instance-admin/edge/propagation')
  return data
}

export async function getEdgeSchedule(): Promise<EdgeSchedule> {
  const { data } = await api.get('/instance-admin/edge/schedule')
  return data
}

export async function updateEdgeSchedule(body: Partial<EdgeSchedule>): Promise<EdgeSchedule> {
  const { data } = await api.put('/instance-admin/edge/schedule', body)
  return data
}

/** The standalone agent file, for copying onto a web server. */
export async function downloadEdgeAgent(): Promise<Blob> {
  const { data } = await api.get('/instance-admin/edge/agent', { responseType: 'blob' })
  return data
}

export async function getEdgeCertificates(): Promise<EdgeCertificate[]> {
  const { data } = await api.get('/instance-admin/edge/certificates')
  return data.certificates
}

export async function uploadEdgeCertificate(payload: {
  cert_pem: string; key_pem: string; chain_pem?: string; source?: 'upload' | 'cloudflare_origin'
}): Promise<EdgeCertificate> {
  const { data } = await api.post('/instance-admin/edge/certificates', payload)
  return data
}

export async function retryEdgeCertificate(id: number): Promise<EdgeCertificate> {
  const { data } = await api.post(`/instance-admin/edge/certificates/${id}/retry`)
  return data
}

export async function removeEdgeCertificate(id: number): Promise<void> {
  await api.delete(`/instance-admin/edge/certificates/${id}`)
}

export async function reconcileEdge(): Promise<Record<string, unknown>> {
  const { data } = await api.post('/instance-admin/edge/reconcile')
  return data
}
